import { useCallback, useEffect, useId, useState } from 'react'

import Action from 'ageas-react-component-action'
import CheckBox from 'ageas-react-component-checkbox'
import Date from 'ageas-react-component-datepicker'
import Dropdown from 'ageas-react-component-dropdownlist'
import Section from 'ageas-react-component-section'
import TextField from 'ageas-react-component-textfield'

import moment from 'moment'
import { Result } from '../../../@types/PolicyHolderDetails'
import { useReportStore } from '../../../Stores/reportStore'
import { useLoadPolicyHolderReferenceDataStore } from '../../../Stores/useLoadPolicyHolderReferenceDataStore'
import { t } from '../../../i18n'
import {
  validateFistNameLength,
  validateFullName,
} from '../../../utils/validates'
import { NIFValidateFiscalNumber } from '../../NIFValidateFiscalNumber'
import { validateMandatoryFields } from '../CreateEntity'
import { DatePickerContainer, StandardContainerCheckbox } from '../style'

export interface FormIdentificationValuesProps {
  vatNumber: string | null
  gender: string
  birthdate: string
  nationality: string
  nationalityId: string
  entityJob: string
  entityJobDescription: string
  eni: string
  name: string
}

type Props = {
  initialOwnerFiscalNumber?: string
  hiddenTitle?: boolean
  onChange: (value: FormIdentificationValuesProps) => void
  initialFormValues: FormIdentificationValuesProps
  onNextChange: () => void
}

export const initialFormIdentificationValues = {
  vatNumber: null,
  gender: '',
  birthdate: '',
  nationality: '',
  nationalityId: '',
  entityJob: '',
  entityJobDescription: '',
  eni: '',
  name: '',
}

const fieldsMandatory = [
  'vatNumber',
  'gender',
  'birthdate',
  'nationality',
  'nationalityId',
  'name',
]

const INITIAL_NATIONALITY = 'PRT'

const initialBirthDate = moment().subtract(18, 'years').format('DD-MM-YYYY')
const pastBirthDate = moment().subtract(90, 'years').format('DD-MM-YYYY')

export function IdentificationForm({
  hiddenTitle = false,
  onChange,
  initialFormValues,
  initialOwnerFiscalNumber = '',
  onNextChange,
}: Props) {
  const id = useId()
  const [validateBirthDate, setValidateBirthDate] = useState(false)
  const [isNamePristine, setIsNamePristine] = useState(false)
  const [formValues, setFormValues] = useState<
    Partial<FormIdentificationValuesProps>
  >({
    ...initialFormValues,
    vatNumber: initialOwnerFiscalNumber,
  })
  const { policyHolderReferenceData } = useLoadPolicyHolderReferenceDataStore()
  const { isProspect } = useReportStore()

  const handleValidateDate = (value: string) => {
    const valueDate = moment(value, 'DD-MM-YYYY')
    const dateToValidate = moment(initialBirthDate, 'DD-MM-YYYY')
    const dateToValidatePast = moment(pastBirthDate, 'DD-MM-YYYY')

    const isValid = valueDate.isBetween(
      dateToValidatePast,
      dateToValidate,
      null,
      '[]',
    )

    setValidateBirthDate(isValid)

    return isValid
  }

  const formatLovValues = (values: Result[]) => {
    return values.map((value) => ({
      caption: value?.description,
      value: value?.idElement,
    }))
  }

  const handleInitialNationality = useCallback(() => {
    return policyHolderReferenceData?.nationalities?.find(
      (item) => item.idElement === INITIAL_NATIONALITY,
    )
  }, [policyHolderReferenceData])

  const handleFormValues = (value: { [key: string]: string }) => {
    setFormValues((prevState) => ({
      ...prevState,
      ...value,
    }))
  }

  const nameMessage = () => {
    const nameLength = (formValues.name || '').length

    if (nameLength > 0) {
      if (!validateFullName(formValues.name || '')) {
        return t('pages.step2.report.errors.invalidName')
      }
      if (!validateFistNameLength(formValues.name || '')) {
        return t('pages.step2.report.errors.invalidFirstNameLength')
      }
    } else if (nameLength === 0 && isNamePristine) {
      return t('pages.step3.mandatory')
    }

    return ''
  }

  useEffect(() => {
    onChange(formValues as never)
  }, [formValues, onChange])

  useEffect(() => {
    if ((formValues.nationality || '')?.length === 0) {
      const nationality = handleInitialNationality()

      handleFormValues({ nationalityId: nationality?.idElement || '' })
      handleFormValues({
        nationality: nationality?.idElement || '',
      })
    }
  }, [formValues, handleInitialNationality, onChange])

  return (
    <>
      {!hiddenTitle && (
        <Section
          id={`${id}createEntityAddressSection`}
          name={t('pages.step3.identification.generalData')}
          config={{ labelSize: '20px', labelBold: '500' }}
          colorTheme="blue"
        />
      )}
      <NIFValidateFiscalNumber
        makeRequest
        readonly={isProspect || (formValues.vatNumber || '')?.length > 0}
        id={`${id}vatNumber`}
        name="vatNumber"
        onChange={(value: string) => handleFormValues({ vatNumber: value })}
        vatNumber={formValues.vatNumber!}
      />
      <TextField
        required
        readOnly
        id={`${id}typeCustomer`}
        name="typeCustomer"
        label={t('pages.step3.identification.clientType')}
        value="Individual"
        onValueChanged={(id: string, value: string) => ({
          typeCustomer: value,
        })}
        colorTheme="blue"
        maxLength={70}
        version="2"
      />
      <StandardContainerCheckbox>
        <CheckBox
          id={`${id}individualNameBusiness`}
          label={t('pages.step3.identification.eni')}
          value={formValues.eni}
          onValueChanged={(id: string, value: string) =>
            handleFormValues({ eni: value })
          }
        />
      </StandardContainerCheckbox>
      <TextField
        id={`${id}-${isNamePristine}-name`}
        key={'generate-report-fullname' + isNamePristine}
        message={nameMessage()}
        name="name"
        label={t('pages.step3.identification.name')}
        value={formValues.name}
        onValueChanged={(id: string, value: string) => {
          setIsNamePristine(true)
          handleFormValues({ name: value })
        }}
        colorTheme="blue"
        maxLength={70}
        version="2"
      />
      <DatePickerContainer>
        <Date
          Picker
          required
          id={`birthdate`}
          name="birthdate"
          label={t('pages.step3.identification.birthdate')}
          format="d-m-Y"
          colorTheme="blue"
          futureDate={initialBirthDate}
          pastDate={pastBirthDate}
          value={formValues.birthdate}
          onValueChanged={(id: string, value: string) => {
            handleValidateDate(value)
            handleFormValues({ birthdate: value })
          }}
          version="2"
        />
      </DatePickerContainer>
      <Dropdown
        required
        id={`${id}gender`}
        name="gender"
        label={t('pages.step3.identification.gender')}
        value={formValues.gender}
        onValueChanged={(id: string, value: string) =>
          handleFormValues({ gender: value })
        }
        colorTheme="blue"
        options={formatLovValues(policyHolderReferenceData?.genders || [])}
        version="2"
      />
      <Dropdown
        required
        id={`${id}nationality`}
        name="nationality"
        label={t('pages.step3.identification.nationalityCountry')}
        value={formValues.nationality}
        onValueChanged={(id: string, value: string) => {
          handleFormValues({ nationalityId: value })
          handleFormValues({
            nationality:
              policyHolderReferenceData?.nationalities?.find(
                (item) => item.idElement === value,
              )?.description || '',
          })
        }}
        colorTheme="blue"
        options={formatLovValues(
          policyHolderReferenceData?.nationalities || [],
        )}
        version="2"
      />
      <Section
        id={`${id}dataIdentification`}
        name={t('pages.step3.identification.additionalInformation')}
        config={{ labelSize: '20px', labelBold: '500' }}
        colorTheme="blue"
      />
      <Dropdown
        id={`${id}entityJob`}
        name="entityJob"
        label={t('pages.step3.identification.profession')}
        value={formValues.entityJob}
        onValueChanged={(id: string, value: string) =>
          handleFormValues({
            entityJob: value,
            entityJobDescription:
              policyHolderReferenceData?.professions?.find(
                (item) => item.idElement === value,
              )?.description || '',
          })
        }
        colorTheme="blue"
        options={formatLovValues(policyHolderReferenceData?.professions || [])}
        version="2"
      />
      <Action
        id="nextEntityCreateButton-1"
        label={t('pages.step3.createEntity.next')}
        type="button"
        disable={
          !validateBirthDate ||
          !validateFullName(formValues.name || '') ||
          !validateFistNameLength(formValues.name || '') ||
          !validateMandatoryFields(
            {
              ...{
                ...formValues,
                vatNumber: formValues.vatNumber || '',
              },
            },
            fieldsMandatory,
          )
        }
        onValueChanged={onNextChange}
      />
    </>
  )
}
